/**
 * This context is purely state for Velocity Insights Chrome extension.
 * TODO: As part of epic VAS-4944, remove this context and avoid
 * tracking state by separating Insights views into their own route.
 */

import { useLocation } from '@reach/router';
import { createContext, useMemo } from 'react';

import { FC, useState, useCallback } from 'react';
import {
  defaultSupportedExtensionIds,
  sendMessageToParentViaWindow,
} from '../utils/extension';

export const EXTENSION_SOURCE = 'extension';
const EXTENSION_CARD = 'extension_card';
const VDP_LIGHT = 'vdp_light';
const VALID_SOURCES = [EXTENSION_SOURCE];

export interface SessionStateContextType {
  inExtensionMode: boolean;
  inExtensionCardMode: boolean;
  isInVDPLightMode: boolean;
  onExtensionCardLoaded: () => void;
  isContextInitialized: boolean;
}

export const SessionStateContext = createContext<SessionStateContextType>({
  inExtensionMode: false,
  inExtensionCardMode: false,
  isInVDPLightMode: false,
  onExtensionCardLoaded: () => undefined,
  isContextInitialized: false,
});

interface SessionStateUrlParameters {
  source: string | null;
  sourceId: string | null;
  purpose: string | null;
}

function getModes({ purpose, source, sourceId }: SessionStateUrlParameters) {
  if (VALID_SOURCES.includes(source || '') && sourceId !== null) {
    const inExtensionMode =
      sourceId && source === EXTENSION_SOURCE
        ? defaultSupportedExtensionIds.includes(sourceId)
        : false;
    return {
      inExtensionMode,
      isInVDPLightMode: inExtensionMode && purpose === VDP_LIGHT,
      inExtensionCardMode: inExtensionMode && purpose === EXTENSION_CARD,
    };
  } else {
    return {
      inExtensionMode: false,
      isInVDPLightMode: false,
      inExtensionCardMode: false,
    };
  }
}

export const SessionStateProvider: FC = ({ children }) => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const { inExtensionCardMode, inExtensionMode, isInVDPLightMode } = getModes({
    source: params.get('source'),
    sourceId: params.get('sourceId'),
    purpose: params.get('purpose'),
  });

  const [extensionCardLoaded, setExtensionCardLoaded] = useState(false);

  const onExtensionCardLoaded = useCallback(() => {
    if (inExtensionCardMode && !extensionCardLoaded) {
      sendMessageToParentViaWindow({ action: 'extension_card_loaded' });
      setExtensionCardLoaded(true);
    }
  }, [inExtensionCardMode, extensionCardLoaded]);

  const value = useMemo(
    () => ({
      inExtensionMode,
      inExtensionCardMode,
      isInVDPLightMode,
      onExtensionCardLoaded,
      isContextInitialized: true,
    }),
    [
      inExtensionCardMode,
      inExtensionMode,
      isInVDPLightMode,
      onExtensionCardLoaded,
    ]
  );

  return (
    <SessionStateContext.Provider value={value}>
      {children}
    </SessionStateContext.Provider>
  );
};
